import Notification from '@rio-cloud/rio-uikit/components/notification/Notification';
import useClipboard from '@rio-cloud/rio-uikit/hooks/useClipboard';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ChatActions } from '@/components/molecule/ChatActions/ChatActions';
import { ActionButtonWrapper, Toolbar } from '@/components/organism/MessageActionBar/MessageActionBar.styles';
import { THandleSendParams } from '@/components/pages/ChatPage/types';
import { ChatFeedbackOptions } from '@/components/template/ChatFeedbackOptions/ChatFeedbackOptions';
import { getUserProfile } from '@/configuration/login/loginSlice';
import { gaPush, MESSAGE_ACTION_BAR_ACTIONS, MESSAGE_ACTION_BAR_CATEGORY } from '@/configuration/setup/googleAnalytics';
import { useSubmitFeedbackMutation } from '@/services/feedbackApi/feedbackApi';
import { selectLastUserMessageBefore, selectMessageById } from '@/store/chat/chat.slice';
import { addNewFeedback, Feedback, updateFeedback } from '@/store/feedback/feedbackSlice';
import { getUserId } from '@/utils/getUserId';
import Logger from '@/utils/logger';
import { forceModalClose } from '@/utils/modalSub';

import { CommentDialog } from '../CommentDialog/CommentDialog';

const LOGGER_TAG = 'MessageActionBar';

type FeedbackIcons = 'helpful' | 'notHelpful' | 'comment' | 'copy';

type IconProps = {
  iconName: FeedbackIcons;
  iconClass: string;
  tooltip: string;
  additionalClass?: string;
  dataTestId?: string;
};

const icons: IconProps[] = [
  {
    iconName: 'helpful',
    iconClass: 'icon-thumbs-up',
    tooltip: 'chatPage.messageActionBar.tooltip.helpful.label',
    additionalClass: 'position-relative',
    dataTestId: 'icon-helpful',
  },
  {
    iconName: 'notHelpful',
    iconClass: 'icon-thumbs-down',
    tooltip: 'chatPage.messageActionBar.tooltip.notHelpful.label',
    additionalClass: 'position-relative',
    dataTestId: 'icon-not-helpful',
  },
  {
    iconName: 'comment',
    iconClass: 'icon-comment',
    tooltip: 'chatPage.messageActionBar.tooltip.comment.label',
    additionalClass: 'text-size-18 position-relative',
    dataTestId: 'icon-comment',
  },
  {
    iconName: 'copy',
    iconClass: 'icon-duplicate',
    tooltip: 'chatPage.chatActions.tooltip.copy.label',
    additionalClass: 'text-size-18 position-relative',
    dataTestId: 'position-relative',
  },
] as const;

type MessageActionBarProps = {
  messageId: string;
  handleSend: (params: THandleSendParams) => void;
};

export const formatAndCopyContent = (answer: { content: string }) => {
  let content = answer.content;

  content = content
    .replace(/```json/g, '')
    .replace(/```/g, '')
    .trim();

  const jsonMatch = content.match(/\{[\s\S]*\}/);

  if (jsonMatch) {
    try {
      const data = JSON.parse(jsonMatch[0]);
      const table = data.table || [];

      if (table.length > 0) {
        const columns = Object.keys(table[0]);

        const tableText = [
          columns.join(' | '),
          ...table.map((row: Record<string, unknown>) => columns.map((col) => row[col]).join(' | ')),
        ].join('\n');

        return content.replace(jsonMatch[0], tableText).trim();
      }
    } catch (error) {
      console.error('Failed JSON:', error);
      return answer.content;
    }
  }

  return answer.content;
};

export const MessageActionBar = ({ messageId, handleSend }: MessageActionBarProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [showDialog, setShowDialog] = useState(false);
  const [activeIcon, setActiveIcon] = useState<Omit<FeedbackIcons, 'comment'> | null>(null);
  const [currentFeedback, setCurrentFeedback] = useState<Feedback | undefined>(undefined);
  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
  const answer = useSelector(selectMessageById(messageId));
  const question = useSelector(selectLastUserMessageBefore(messageId));
  const clipboard = useClipboard();
  const user = useSelector(getUserProfile);
  const userId = getUserId(user?.sub ?? '');

  const handleClose = () => {
    setShowDialog(false);
    setCurrentFeedback(undefined);
    forceModalClose();
  };

  const handleCopy = () => {
    const content = formatAndCopyContent(answer);
    clipboard.copy(content);
    Notification.success(intl.formatMessage({ id: 'chatPage.messageActionBar.tooltip.copy.notification' }));

    gaPush({
      category: MESSAGE_ACTION_BAR_CATEGORY,
      action: MESSAGE_ACTION_BAR_ACTIONS.DOWNLOAD_PDF,
      label: `Answer ID: ${answer.messageId}`,
    });
  };

  const sendFeedback = async (feedbackWithId: Feedback) => {
    try {
      if (!feedbackWithId) return;
      const result = await submitFeedback(feedbackWithId).unwrap();

      if (result) Notification.success(intl.formatMessage({ id: 'common.feedback.success.notification' }));
    } catch (error) {
      Notification.error(intl.formatMessage({ id: 'common.feedback.error.notification' }));
      Logger.error(LOGGER_TAG, 'Failed to send feedback', error);
    }
  };

  const handleSendFeedback = async (iconName: FeedbackIcons) => {
    if (iconName === 'copy') {
      handleCopy();
      return;
    }

    const feedback: Omit<Feedback, 'id'> = {
      lastQuestionId: question ? question.messageId : null,
      lastAnswerId: answer.messageId,
      detail: '',
      rate: iconName === 'helpful' ? 5 : 1,
      userId,
      follow_ups: answer.follow_ups,
    };

    const result = dispatch(addNewFeedback(feedback));
    const newFeedback = result.payload as Feedback;

    if (iconName !== 'comment') {
      setActiveIcon(iconName);
    }

    setCurrentFeedback(newFeedback);

    if (feedback.rate === 1) {
      setShowDialog(true);

      gaPush({
        category: MESSAGE_ACTION_BAR_CATEGORY,
        action: MESSAGE_ACTION_BAR_ACTIONS.THUMBS_DOWN,
        label: `Answer ID: ${answer.messageId}`,
      });
    } else if (feedback.rate === 5) {
      gaPush({
        category: MESSAGE_ACTION_BAR_CATEGORY,
        action: MESSAGE_ACTION_BAR_ACTIONS.THUMBS_UP,
        label: `Answer ID: ${answer.messageId}`,
      });
      sendFeedback(newFeedback);
    } else if (iconName === 'comment') {
      gaPush({
        category: MESSAGE_ACTION_BAR_CATEGORY,
        action: MESSAGE_ACTION_BAR_ACTIONS.COMMENT,
        label: `Answer ID: ${answer.messageId}`,
      });
    }
  };

  const handleUpdateDetails = async (details: string) => {
    setShowDialog(false);
    forceModalClose();

    if (currentFeedback) {
      const updatedFeedback = {
        ...currentFeedback,
        detail: details,
        userId,
      };
      dispatch(updateFeedback(updatedFeedback));
      await sendFeedback(updatedFeedback);
    }
  };

  const handleSendAndManagePopover = (content: string, isOnboarding: boolean) => {
    handleSend({ content, isOnboarding });
  };

  return (
    <ActionButtonWrapper>
      <Toolbar data-testid="feedback-options-container">
        <>
          {icons.map((icon) => (
            <ChatFeedbackOptions
              key={icon.iconName}
              iconName={icon.iconName}
              iconClass={icon.iconClass}
              tooltip={icon.tooltip}
              additionalClass={icon.additionalClass}
              clickAction={() => handleSendFeedback(icon.iconName)}
              isActive={activeIcon === icon.iconName}
              dataTestId={icon.dataTestId}
            />
          ))}
          <div>
            <ChatActions
              handleSend={handleSendAndManagePopover}
              message={answer?.content ?? ''}
              question={question?.content ?? ''}
            />
          </div>
        </>
      </Toolbar>

      <CommentDialog
        showDialog={showDialog}
        handleClose={handleClose}
        question={question?.content ?? ''}
        answer={answer?.content ?? ''}
        updateDetails={handleUpdateDetails}
        isLoading={isLoading}
        messageId={messageId}
        data_visualization={answer?.data_visualization}
      />
    </ActionButtonWrapper>
  );
};
